import { Alert, Button, Spin, Tag } from "antd";
import { useState } from "react";
import { Amount, ColumnType, Table } from "src/components";
import ChannelLogo from "src/components/TableColumns/ChannelLogo";
import CopyableRef from "src/components/TableColumns/CopyableRef";
import SportGameName from "src/components/TableColumns/SportGameName";
import { colors } from "src/constants";
import { useCustomerGameSessions, useGameSessionRounds } from "src/hooks";
import { CustomerLog, CustomerLogType, GameSession, GameSessionSummary } from "src/types";
import { datetime, decimal } from "src/utils";
import { addCommas } from "src/utils/number/number";
import { AmountColor, getGameChannel, getGameProviderName } from "./helpers";

const ROUNDS_PREVIEW = 5;
// While wallet prepares a customer's cache in the background, the tab asks again this often.
const PREPARING_POLL_MS = 3000;
// Wallet rejects longer ranges: sessions are stitched per request and old ranges are computed live.
const MAX_RANGE_DAYS = 31;

const subLineStyle: React.CSSProperties = { fontSize: 12, color: colors.cloud7 };

function money(value: number): string {
  return addCommas(decimal(value));
}

function sessionKey(session: GameSession): string {
  return [session.gameProvider, session.betTransactionRefId || session.gameName, session.startedAt].join("|");
}

/** "+1 วัน" when `to` falls on a later Thai calendar day than `from`. */
function DayShift({ from, to }: { from: string; to?: string | null }) {
  if (!to) return null;
  const days = datetime(to).startOf("day").diff(datetime(from).startOf("day"), "day");
  return days > 0 ? <Tag style={{ marginLeft: 6 }}>{`+${days} วัน`}</Tag> : null;
}

function SessionTime({ session }: { session: GameSession }) {
  const pending = session.pendingCount > 0;
  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <div>
        <b>{datetime(session.startedAt).format("HH:mm:ss")}</b>
        {pending ? (
          <Tag color="orange" style={{ marginLeft: 6 }}>รอผล</Tag>
        ) : (
          <>
            {" – "}
            {session.endedAt ? datetime(session.endedAt).format("HH:mm:ss") : "—"}
            <DayShift from={session.startedAt} to={session.endedAt} />
          </>
        )}
      </div>
      <div style={subLineStyle}>{datetime(session.startedAt).format("DD/MM/YYYY")}</div>
    </div>
  );
}

function SessionGameName({ session }: { session: GameSession }) {
  return (
    <div>
      {session.betTransactionRefId ? (
        <SportGameName gameName={session.gameName || undefined} detail={session.sportDetail} />
      ) : (
        <div>{session.gameName || "-"}</div>
      )}
      <div>
        {session.cancelledCount > 0 && (
          <Tag color="red">{session.betTransactionRefId ? "ยกเลิกบิล" : `ยกเลิก ${session.cancelledCount} ตา`}</Tag>
        )}
        {session.betCoin > 0 && <Tag color="purple">เหรียญโบนัส</Tag>}
        {session.featureBuyCount > 0 && <Tag color="cyan">{`ซื้อฟีเจอร์ ${session.featureBuyCount} ตา`}</Tag>}
      </div>
      {session.freeSpinCount > 0 && (
        <div style={subLineStyle}>{`มีฟรีสปิน ${session.freeSpinCount} ตา ที่ไม่ตัดเงินเดิมพัน`}</div>
      )}
    </div>
  );
}

function SessionNet({ session }: { session: GameSession }) {
  // A whole cancelled sport bill; a game session's cancelled rounds are already netted out by wallet.
  if (session.betTransactionRefId && session.cancelledCount > 0) {
    return (
      <div style={{ textAlign: "right" }}>
        <div>0.00</div>
        <div style={subLineStyle}>คืนเงินเต็ม</div>
      </div>
    );
  }
  if (session.net === null) return <div style={{ textAlign: "right" }}>—</div>;
  return <AmountColor value={session.net} />;
}

function SummaryBar({ summary }: { summary: GameSessionSummary }) {
  const notes = [
    summary.pendingCount > 0 && `ยังมี ${summary.pendingCount} บิลที่รอผล ยอดสุทธิจึงยังไม่นิ่ง`,
    summary.cancelledCount > 0 && `ไม่นับบิลที่ถูกยกเลิก ${summary.cancelledCount} บิล`,
  ].filter(Boolean);
  return (
    <div
      style={{
        display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "space-between", alignItems: "center",
        padding: "8px 12px", marginBottom: 12, border: `1px solid ${colors.cloud3}`, borderRadius: 6,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
        <span>รอบการเล่น <b>{`${summary.sessionCount} รอบ`}</b></span>
        <span>ตา/บิลทั้งหมด <b>{`${summary.roundCount} รายการ`}</b></span>
        <span>ยอดเดิมพันรวม <b>{money(summary.betTotal)}</b></span>
        <span>ยอดที่ชนะรวม <b>{money(summary.payoutTotal)}</b></span>
        <span style={{ display: "inline-flex", gap: 6 }}>ได้/เสียสุทธิ <b><AmountColor value={summary.netTotal} /></b></span>
      </div>
      <div style={subLineStyle}>{notes.join(" · ")}</div>
    </div>
  );
}

function isSportRow(record: CustomerLog): boolean {
  return Boolean(record.betAt) || (record.transactionType === CustomerLogType.VOID && Boolean(record.gameProvider));
}

function RoundBet({ record }: { record: CustomerLog }) {
  const cash = Math.min(record.amount || 0, 0);
  const coin = Math.min(record.coinBonusAmount || 0, 0);
  if (record.transactionType === CustomerLogType.VOID) return <div style={{ textAlign: "right" }}>0.00</div>;
  if (cash === 0 && coin === 0) {
    return <div style={{ textAlign: "right" }}>{isSportRow(record) ? "0.00" : "—"}</div>;
  }
  return (
    <div style={{ textAlign: "right" }}>
      <AmountColor value={cash + coin} />
      {coin < 0 && (
        <div style={subLineStyle}>
          {cash < 0 ? `โบนัส ${money(-coin)} · เงินสด ${money(-cash)}` : `โบนัส ${money(-coin)}`}
        </div>
      )}
    </div>
  );
}

function roundTag(record: CustomerLog) {
  if (record.transactionType === CustomerLogType.VOID) return <Tag color="red">ยกเลิก</Tag>;
  if (isSportRow(record)) {
    return record.payoutTransactionRefId ? <Tag color="purple">ผลออก</Tag> : <Tag color="orange">แทง</Tag>;
  }
  if (record.isFeatureBuy) return <Tag color="cyan">ซื้อฟีเจอร์</Tag>;
  if (!record.amount && !record.coinBonusAmount) return <Tag color="gold">ฟรีสปิน</Tag>;
  return null;
}

const roundColumns: ColumnType<CustomerLog>[] = [
  {
    title: "เวลาแทง",
    key: "betTime",
    width: 110,
    render: (_, record) => {
      // A sport bill's payout / void row is shown at its own time only, as in the wallet tab's paired rows.
      if (isSportRow(record) && (record.payoutTransactionRefId || record.transactionType === CustomerLogType.VOID)) {
        return "—";
      }
      return datetime(record.betAt ?? record.createdAt).format("HH:mm:ss");
    },
  },
  {
    title: "เวลาผลออก",
    key: "settleTime",
    width: 130,
    render: (_, record) => {
      const betTime = record.betAt ?? record.createdAt;
      if (isSportRow(record)) {
        if (!record.payoutTransactionRefId && record.transactionType !== CustomerLogType.VOID) return "—";
        return (
          <>
            {datetime(record.createdAt).format("HH:mm:ss")}
            <DayShift from={betTime} to={record.createdAt} />
          </>
        );
      }
      if (!record.settledAt) return "—";
      return (
        <>
          {datetime(record.settledAt).format("HH:mm:ss")}
          <DayShift from={betTime} to={record.settledAt} />
        </>
      );
    },
  },
  { title: "รายการ", key: "kind", width: 90, render: (_, record) => roundTag(record) },
  { title: "เงินเดิมพัน", key: "bet", width: 170, render: (_, record) => <RoundBet record={record} /> },
  {
    title: "เงินที่ชนะ",
    key: "payout",
    width: 120,
    render: (_, record) => {
      if (record.transactionType === CustomerLogType.VOID) {
        // A cancelled bet comes back (+); a voided payout is taken back (-). Shown by kind, since AWC writes the
        // cash of a voided payout as a positive amount.
        const total = Math.abs(record.amount || 0) + Math.abs(record.coinBonusAmount || 0);
        return <AmountColor value={record.betTransactionRefId ? total : -total} />;
      }
      if (isSportRow(record) && !record.payoutTransactionRefId) return <div style={{ textAlign: "right" }}>—</div>;
      return <AmountColor value={record.payoutAmount || 0} />;
    },
  },
  {
    title: "ยอดเงินหลังทำรายการ",
    dataIndex: "afterBalance",
    width: 150,
    render: (value) => <Amount value={value} />,
  },
  {
    title: "รหัสอ้างอิงของค่ายเกม",
    dataIndex: "parentBetId",
    render: (value) => <CopyableRef value={value} />,
  },
];

function SessionRounds({ customerCode, session }: { customerCode: string; session: GameSession }) {
  const [showAll, setShowAll] = useState(false);
  const { data: rounds, isFetching } = useGameSessionRounds(customerCode, session);
  const visible = showAll ? rounds : rounds?.slice(0, ROUNDS_PREVIEW);
  return (
    <div>
      <Table
        rowKey="uid"
        size="small"
        columns={roundColumns}
        dataSource={visible || []}
        loading={isFetching}
        pagination={false}
        keepShowTable
      />
      {rounds && rounds.length > ROUNDS_PREVIEW && (
        <div style={{ ...subLineStyle, padding: "6px 8px" }}>
          {showAll ? `แสดงทั้งหมด ${rounds.length} ตา ` : `แสดง ${ROUNDS_PREVIEW} จาก ${rounds.length} ตา `}
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowAll(!showAll)}>
            {showAll ? "ย่อ" : "ดูทั้งรอบ"}
          </Button>
        </div>
      )}
    </div>
  );
}

export interface GameSessionTabProps {
  customerCode: string;
  startDate?: string;
  endDate?: string;
  gameProviders?: string[];
}

/** "รอบการเล่นรายเกม" tab: the customer's play grouped into sessions per game (sport: one bill per row). */
function GameSessionTab({ customerCode, startDate, endDate, gameProviders }: GameSessionTabProps) {
  const [paging, setPaging] = useState({ skip: 0, limit: 100 });
  const [preparing, setPreparing] = useState(false);
  const rangeTooLong = Boolean(startDate && endDate)
    && datetime(endDate as string).diff(datetime(startDate as string), "day", true) > MAX_RANGE_DAYS;
  const { data, isFetching, refetch } = useCustomerGameSessions(
    customerCode,
    { ...paging, startDate, endDate, gameProviders },
    {
      enabled: Boolean(customerCode && startDate && endDate) && !rangeTooLong,
      keepPreviousData: true,
      refetchInterval: preparing ? PREPARING_POLL_MS : false,
      onSuccess: (page) => setPreparing(page.status === "preparing"),
    }
  );

  if (rangeTooLong) {
    return <Alert type="warning" showIcon message={`ดูรอบการเล่นได้ครั้งละไม่เกิน ${MAX_RANGE_DAYS} วัน กรุณาเลือกช่วงวันที่ใหม่`} />;
  }

  const columns: ColumnType<GameSession>[] = [
    { title: "เวลาเริ่ม – สิ้นสุด", key: "time", width: 200, render: (_, session) => <SessionTime session={session} /> },
    {
      title: "ชื่อเกม / รายละเอียดการเดิมพัน",
      key: "game",
      render: (_, session) => <SessionGameName session={session} />,
    },
    {
      title: "ช่องทาง",
      key: "channel",
      width: 90,
      render: (_, session) => <ChannelLogo channel={getGameChannel(session.gameProvider)} />,
    },
    {
      title: "ค่ายเกม",
      key: "gameProvider",
      width: 140,
      render: (_, session) => getGameProviderName(session.gameProvider),
    },
    { title: "จำนวนตา/บิล", dataIndex: "roundCount", align: "right", width: 110 },
    {
      title: "ยอดเดิมพันรวม",
      key: "bet",
      width: 130,
      render: (_, session) => <Amount value={session.betCash + session.betCoin} />,
    },
    {
      title: "ยอดที่ชนะรวม",
      key: "payout",
      width: 130,
      render: (_, session) => (session.pendingCount > 0
        ? <div style={{ textAlign: "right" }}>—</div>
        : <Amount value={session.payout} allowZero />),
    },
    { title: "ได้/เสียสุทธิ", key: "net", width: 130, render: (_, session) => <SessionNet session={session} /> },
  ];

  if (data?.status === "preparing") {
    return (
      <Alert
        type="info"
        showIcon
        icon={<Spin size="small" />}
        message="กำลังเตรียมข้อมูลรอบการเล่นของลูกค้ารายนี้"
        description={`ลูกค้าเล่นเยอะ ช่วงวันที่นี้ต้องเตรียมข้อมูลก่อน (เตรียมแล้ว ${data.preparedDays ?? 0} จาก ${data.totalDays ?? 0} วัน) หน้านี้จะแสดงผลเองเมื่อพร้อม และครั้งต่อไปจะเปิดได้ทันที`}
      />
    );
  }
  if (data?.status === "failed") {
    return (
      <Alert
        type="error"
        showIcon
        message="เตรียมข้อมูลรอบการเล่นไม่สำเร็จ"
        description="กรุณาลองใหม่อีกครั้งในอีกสักครู่"
        action={<Button size="small" onClick={() => refetch()}>ลองใหม่</Button>}
      />
    );
  }

  return (
    <>
      {data?.summary && <SummaryBar summary={data.summary} />}
      <Table
        rowKey={sessionKey}
        columns={columns}
        dataSource={data ? data.results : undefined}
        loading={{ size: "large", spinning: isFetching }}
        expandable={{
          expandedRowRender: (session) => <SessionRounds customerCode={customerCode} session={session} />,
        }}
        pagination={{
          total: data?.total,
          current: paging.skip / paging.limit + 1,
          pageSize: paging.limit,
          showTotal: (total) => `รอบการเล่นทั้งหมด ${total}`,
          onChange: (page, pageSize) => setPaging({ skip: (page - 1) * (pageSize || paging.limit), limit: pageSize || paging.limit }),
        }}
      />
    </>
  );
}

export default GameSessionTab;
